<template>
  <div class="scheduled-meetings-card floating-widget">
    <!-- Widget Header -->
    <div class="card-header">
      <div class="card-title-group">
        <div class="title-icon-badge">
          <i class="fas fa-calendar-check"></i>
        </div>
        <div>
          <h2 class="card-title">Scheduled Meetings</h2>
          <p class="card-subtitle">Upcoming Google Calendar meetings synced with your Spark leads</p>
        </div>
      </div>

      <div class="status-summary-pills">
        <div
          v-if="!isGoogleConfigured && !isLoading"
          class="google-not-configured-badge"
          title="Set GOOGLE_SERVICE_ACCOUNT_JSON to enable live calendar sync"
        >
          <i class="fas fa-exclamation-circle"></i> Google Calendar not configured
        </div>
        <span class="summary-pill scheduled-pill">
          <i class="fas fa-calendar-check"></i> {{ upcomingCount }} Scheduled
        </span>
      </div>
    </div>

    <!-- Schedule Toolbar / Controls -->
    <div class="list-toolbar">
      <div class="toolbar-left">
        <span class="toolbar-section-label">
          <i class="fas fa-calendar-alt me-1"></i> Upcoming Schedule
        </span>
      </div>

      <div class="controls">
        <div class="control-group">
          <label for="sort-by">Sort By:</label>
          <select id="sort-by" v-model="sortBy" class="form-select">
            <option value="date_desc">Date (Newest)</option>
            <option value="date_asc">Date (Oldest)</option>
            <option value="name">Name</option>
          </select>
        </div>
        <div class="control-group">
          <label for="rows-per-page">Rows per page:</label>
          <select id="rows-per-page" v-model.number="pagination.pageSize" class="form-select">
            <option :value="5">5</option>
            <option :value="10">10</option>
            <option :value="20">20</option>
            <option :value="25">25</option>
          </select>
        </div>

        <div class="pagination">
          <button type="button" @click="prevPage" :disabled="pagination.currentPage === 1" class="pagination-arrow">
            <i class="fas fa-chevron-left"></i>
          </button>
          <span class="pagination-label">
            <strong>{{ paginationRange }}</strong> of {{ paginationTotal }}
          </span>
          <button type="button" @click="nextPage" :disabled="pagination.currentPage >= totalPages" class="pagination-arrow">
            <i class="fas fa-chevron-right"></i>
          </button>
        </div>
      </div>
    </div>

    <!-- Google Calendar setup banner -->
    <div v-if="!isGoogleConfigured && !isLoading" class="setup-banner">
      <i class="fab fa-google"></i>
      <div>
        <strong>Google Calendar Integration Ready</strong>
        <p>
          To activate live meeting sync: create a Google Cloud project, add a Service Account with domain-wide
          delegation, then set the <code>GOOGLE_SERVICE_ACCOUNT_JSON</code> environment variable on the server. See
          <code>services/googleCalendarService.go</code> for full setup instructions.
        </p>
      </div>
    </div>

    <div class="meetings-list">
      <div v-if="isLoading" class="loading-state">
        <i class="fas fa-spinner fa-spin me-2"></i> Loading scheduled meetings...
      </div>
      <div v-else-if="paginatedMeetings.length === 0" class="empty-state">
        <i class="far fa-calendar-times empty-icon"></i>
        <p class="empty-title">No scheduled meetings found.</p>
        <p class="empty-sub">Upcoming meetings synced from Google Calendar will appear here.</p>
      </div>
      <div
        v-else
        v-for="meeting in paginatedMeetings"
        :key="meeting.id"
        class="meeting-row"
        :class="{ cancelled: meeting.status === 'cancelled' }"
      >
        <div class="meeting-date">
          <div class="date-formatted">{{ formatDate(meeting.meeting_datetime) }}</div>
          <div class="time-formatted">{{ formatTime(meeting.meeting_datetime) }}</div>
        </div>
        <div class="meeting-info">
          <div class="meeting-name">{{ meeting.client_name }}</div>
          <div class="meeting-contact">
            <i class="fas fa-phone-alt me-1 text-muted"></i>{{ meeting.client_phone || '—' }}
          </div>
          <div class="meeting-contact">
            <i class="far fa-envelope me-1 text-muted"></i>{{ meeting.client_email }}
          </div>
        </div>
        <div class="meeting-bdm">
          <div class="bdm-label">BDM</div>
          <div class="bdm-name">{{ meeting.bdm_name || '—' }}</div>
        </div>
        <div class="meeting-status">
          <span class="status-badge" :class="meeting.status">{{ meeting.status }}</span>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'ScheduledMeetingsWidget',
  emits: ['count-updated'],
  data() {
    return {
      isLoading: true,
      isGoogleConfigured: false,
      meetings: [],
      sortBy: 'date_asc',
      pagination: {
        currentPage: 1,
        pageSize: 5,
      },
    };
  },
  computed: {
    upcomingCount() {
      return this.meetings.filter((m) => m.status !== 'cancelled').length;
    },
    sortedMeetings() {
      const sorted = [...this.meetings];
      sorted.sort((a, b) => {
        if (this.sortBy === 'name') return (a.client_name || '').localeCompare(b.client_name || '');
        if (this.sortBy === 'date_desc') return new Date(b.meeting_datetime) - new Date(a.meeting_datetime);
        if (this.sortBy === 'date_asc') return new Date(a.meeting_datetime) - new Date(b.meeting_datetime);
        return 0;
      });
      return sorted;
    },
    paginatedMeetings() {
      const start = (this.pagination.currentPage - 1) * this.pagination.pageSize;
      return this.sortedMeetings.slice(start, start + this.pagination.pageSize);
    },
    totalPages() {
      return Math.max(1, Math.ceil(this.sortedMeetings.length / this.pagination.pageSize));
    },
    paginationRange() {
      const total = this.sortedMeetings.length;
      if (total === 0) return '0';
      const start = (this.pagination.currentPage - 1) * this.pagination.pageSize + 1;
      const end = Math.min(start + this.pagination.pageSize - 1, total);
      return `${start}-${end}`;
    },
    paginationTotal() {
      return this.sortedMeetings.length;
    },
  },
  watch: {
    'pagination.pageSize'() {
      this.pagination.currentPage = 1;
    },
    sortBy() {
      this.pagination.currentPage = 1;
    },
  },
  methods: {
    formatDate(dateString) {
      if (!dateString) return 'N/A';
      const date = new Date(dateString);
      if (Number.isNaN(date.getTime())) return 'N/A';
      return date.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: '2-digit' });
    },
    formatTime(dateString) {
      if (!dateString) return '';
      const date = new Date(dateString);
      if (Number.isNaN(date.getTime())) return '';
      return date.toLocaleTimeString('en-US', {
        hour: '2-digit',
        minute: '2-digit',
        hour12: true,
      });
    },
    nextPage() {
      if (this.pagination.currentPage < this.totalPages) this.pagination.currentPage++;
    },
    prevPage() {
      if (this.pagination.currentPage > 1) this.pagination.currentPage--;
    },
    async fetchMeetings() {
      this.isLoading = true;
      try {
        const response = await this.$axios.get('/bdm-bookings');
        if (response.data.success) {
          this.meetings = response.data.bookings || [];
          this.isGoogleConfigured = response.data.is_google_configured || false;
        }
      } catch (error) {
        console.error('Failed to fetch BDM meetings:', error);
        this.meetings = [];
      } finally {
        this.isLoading = false;
        this.$emit('count-updated', this.upcomingCount);
      }
    },
  },
  created() {
    this.fetchMeetings();
  },
};
</script>

<style scoped>
.scheduled-meetings-card {
  background-color: #ffffff;
  border-radius: 16px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
  border: 1px solid #e9ecef;
  padding: 0;
  font-family: 'Roboto', sans-serif;
  width: 100%;
  max-width: 100%;
}

.floating-widget {
  position: relative;
  width: 100%;
  max-width: 100%;
  flex-grow: 1;
  z-index: 10;
}

/* Header */
.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 18px 24px;
  border-bottom: 1px solid #e9ecef;
  flex-wrap: wrap;
  gap: 12px;
}

.card-title-group {
  display: flex;
  align-items: center;
  gap: 14px;
}

.title-icon-badge {
  width: 42px;
  height: 42px;
  border-radius: 12px;
  background-color: #DAF3F9;
  color: #00A0C1;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.15rem;
  flex-shrink: 0;
}

.card-title {
  font-size: 1.25rem;
  font-weight: 700;
  color: #435666;
  margin: 0;
}

.card-subtitle {
  margin: 2px 0 0 0;
  font-size: 0.85rem;
  color: #5C768B;
}

.status-summary-pills {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
}

.summary-pill {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 14px;
  border-radius: 9999px;
  font-size: 0.82rem;
  font-weight: 700;
  border: 1px solid transparent;
}

.scheduled-pill {
  background-color: #DAF3F9;
  color: #00A0C1;
}

/* Google not configured badge */
.google-not-configured-badge {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: #FFDEC3;
  color: #d97724;
  border: 1px solid #ffd5b0;
  border-radius: 9999px;
  padding: 6px 14px;
  font-size: 0.8rem;
  font-weight: 700;
  cursor: help;
}

/* List Toolbar */
.list-toolbar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 14px 24px;
  border-bottom: 1px solid #e9ecef;
  flex-wrap: wrap;
  gap: 12px;
}

.toolbar-left {
  display: flex;
  align-items: center;
  gap: 16px;
  flex-wrap: wrap;
}

.toolbar-section-label {
  font-size: 0.95rem;
  font-weight: 700;
  color: #435666;
}

/* Controls */
.controls {
  display: flex;
  align-items: center;
  gap: 16px;
  flex-wrap: wrap;
}

.control-group {
  display: flex;
  align-items: center;
  gap: 8px;
}

.control-group label {
  color: #5C768B;
  font-size: 0.88rem;
  font-weight: 500;
  white-space: nowrap;
}

.form-select {
  -webkit-appearance: none;
  -moz-appearance: none;
  appearance: none;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 16 16'%3E%3Cpath fill='none' stroke='%23343a40' stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='m2 5 6 6 6-6'/%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: right 0.75rem center;
  background-size: 16px 12px;
  padding: 6px 36px 6px 12px;
  border: 1px solid #dee2e6;
  border-radius: 6px;
  font-size: 0.88rem;
  font-weight: 500;
  color: #435666;
  background-color: #fff;
}

/* Pagination */
.pagination {
  display: inline-flex;
  align-items: center;
  border: 1px solid #dee2e6;
  border-radius: 8px;
  background-color: #fff;
  overflow: hidden;
}

.pagination-label {
  font-size: 0.88rem;
  color: #5C768B;
  font-weight: 500;
  padding: 0 8px;
  white-space: nowrap;
}

.pagination-label strong {
  color: #435666;
}

.pagination-arrow {
  background-color: transparent;
  border: none;
  cursor: pointer;
  color: #00A0C1;
  font-size: 0.95rem;
  padding: 6px 12px;
  transition: background-color 0.2s ease;
}

.pagination-arrow:hover:not(:disabled) {
  background-color: #f1f3f5;
}

.pagination-arrow:disabled {
  color: #ced4da;
  cursor: not-allowed;
}

/* Setup banner */
.setup-banner {
  display: flex;
  align-items: flex-start;
  gap: 14px;
  background: #f0f9ff;
  border-left: 4px solid #00A0C1;
  border-bottom: 1px solid #e9ecef;
  padding: 14px 24px;
  font-size: 0.85rem;
  color: #435666;
}

.setup-banner .fa-google {
  font-size: 1.6rem;
  color: #00A0C1;
  margin-top: 2px;
  flex-shrink: 0;
}

.setup-banner strong {
  display: block;
  margin-bottom: 4px;
  font-size: 0.9rem;
  color: #435666;
}

.setup-banner p {
  margin: 0;
  line-height: 1.5;
  color: #5C768B;
}

.setup-banner code {
  background: #DAF3F9;
  color: #00829e;
  padding: 1px 6px;
  border-radius: 4px;
  font-family: monospace;
  font-size: 0.8rem;
}

/* Meetings list */
.meetings-list {
  display: flex;
  flex-direction: column;
  gap: 10px;
  max-height: 400px;
  overflow-y: auto;
  padding: 16px 24px;
}

.meeting-row {
  display: flex;
  align-items: stretch;
  background-color: #ffffff;
  border-radius: 12px;
  border: 1px solid #e9ecef;
  flex-shrink: 0;
  overflow: hidden;
  transition: all 0.2s ease;
}

.meeting-row:hover {
  border-color: #00A0C1;
  box-shadow: 0 3px 10px rgba(0, 160, 193, 0.08);
}

.meeting-row.cancelled {
  opacity: 0.65;
  border-color: #f5c6cb;
  background: #fff5f5;
}

.meeting-date {
  background-color: #435666;
  color: #ffffff;
  min-width: 130px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 14px 12px;
  text-align: center;
  flex-shrink: 0;
}

.date-formatted {
  font-weight: 700;
  font-size: 0.85rem;
  text-transform: uppercase;
}

.time-formatted {
  font-size: 0.78rem;
  color: #dbe4ec;
  margin-top: 2px;
}

.meeting-info {
  flex-grow: 1;
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  align-items: center;
  gap: 12px;
  padding: 14px 20px;
}

.meeting-name {
  font-weight: 700;
  font-size: 1.02rem;
  color: #435666;
}

.meeting-contact {
  color: #5C768B;
  font-size: 0.88rem;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

/* BDM column */
.meeting-bdm {
  min-width: 130px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  padding: 12px 16px;
  border-left: 1px solid #e9ecef;
}

.bdm-label {
  font-size: 0.7rem;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: #5C768B;
  font-weight: 700;
}

.bdm-name {
  font-size: 0.88rem;
  font-weight: 600;
  color: #435666;
}

/* Status badge */
.meeting-status {
  display: flex;
  align-items: center;
  padding: 0 18px 0 8px;
}

.status-badge {
  display: inline-flex;
  align-items: center;
  font-size: 0.74rem;
  font-weight: 700;
  padding: 3px 10px;
  border-radius: 9999px;
  text-transform: uppercase;
}

.status-badge.scheduled {
  background: #D1FAE5;
  color: #00A54F;
}

.status-badge.cancelled {
  background: #FEE2E2;
  color: #DC2626;
}

/* Empty & Loading States */
.loading-state,
.empty-state {
  text-align: center;
  padding: 36px 20px;
  color: #5C768B;
}

.empty-icon {
  font-size: 2rem;
  color: #cbd5e1;
  margin-bottom: 8px;
  display: block;
}

.empty-title {
  font-weight: 600;
  color: #435666;
  margin: 0 0 4px 0;
}

.empty-sub {
  font-size: 0.85rem;
  color: #5C768B;
  margin: 0;
}
</style>